import { Box, CircularProgress, Divider, InputAdornment, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import classNames from 'classnames';
import { SGSForm, SGSFormInput, SGSFormInputMask, SGSRadioCtr } from 'components';
import { useAppContext } from 'core/contexts';
import { accesWebOptions, roles, titres, TypeMode, TypeRole } from 'core/models';
import { hasRole } from 'core/utils/roles';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUtilisateurFromContext } from '../../contexts/UtilisateurFormProvider';
import { useUserInfosContext } from '../../contexts/UtilisateurInfosProvider';
import styles from '../Utilisateurs.module.scss';

type Props = {
    watch: Function;
    setValue: Function;
    getValues: Function;
    mode?: TypeMode;
    isCurrentUser?: boolean;
    trigger: Function;
};

let timeoutId: NodeJS.Timeout;
const debounce = (func: { (): Promise<void>; (): void }, delay: number | undefined) => {
    return function () {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func();
        }, delay);
    };
};

export const FormInfos: React.FC<PropsWithChildren<Props>> = ({ watch, setValue, getValues, mode = 'create', isCurrentUser = false, trigger }) => {
    const { t } = useTranslation();
    const { control } = useFormContext();
    const { user } = useAppContext();
    const { selectedUser, isCheckingLogin, isCheckingEmail, setIsValidatedEmail, setIsValidatedLogin } = useUtilisateurFromContext();
    const { role, setRole, responsables } = useUserInfosContext();
    const [email, setEmail] = useState();

    const watchResponsable = watch('responsable');
    const watchEmail = watch('email', false);

    useEffect(
        () => {
            setValue('login', getValues('email'));
            handleEmailChange();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [watchEmail],
    );

    const rolesList = [...roles];
    if ((selectedUser.role !== 'ADMIN' || mode === 'create') && mode !== 'newUser' && mode !== 'profil') delete rolesList[0];

    useEffect(
        () => {
            // Set user has responsable for creation
            if ((user.roles.includes('ROLE_RESPONSABLE') || user.roles.includes('ROLE_ADMIN')) && mode === 'create') {
                const responsable = responsables.find((e) => e.id === user.id);
                setValue('responsable', responsable);
            }
            if (mode === 'newUser') setRole('ADMIN');
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [role],
    );

    const handleResponsableSelection = (value) => {
        setValue('responsable', value, { shouldDirty: true });
        trigger('responsable');
    };

    // NOTE : if login is not mapped to email, reactivate handleLoginChange and remove setIsValidatedLogin from validateEmail !

    const validateEmail = () => {
        setIsValidatedEmail(false);
        setIsValidatedLogin(false);
        trigger('email');
        trigger('login');
    };

    const handleEmailChange = async () => {
        if (!email || email === getValues('email')) return;
        await new Promise((resolve) => {
            debounce(async () => {
                resolve(validateEmail());
            }, 500)();
        });
    };

    // const validateLogin = () => {
    //     setIsValidatedEmail(false);
    //     trigger('login');
    // };

    // const handleLoginChange = async () => {
    //     await new Promise((resolve) => {
    //         debounce(async () => {
    //             resolve(validateLogin());
    //         }, 500)();
    //     });
    // };

    useEffect(
        () => {
            setEmail(getValues('email'));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <>
            {/* TODO :: add link to contact form  */}
            {hasRole(user, ['ROLE_ADMIN']) && (mode === 'create' || mode === 'profil' || mode === 'update') && selectedUser.role === 'ADMIN' && (
                <SGSForm.Row>
                    <SGSForm.Col>
                        <Box className="mt-2 mb-3">
                            <Alert severity="info">
                                Pour changer d'administrateur, veuillez <Link to={'/contact'}>contacter le support</Link>.
                            </Alert>
                        </Box>
                    </SGSForm.Col>
                </SGSForm.Row>
            )}

            {mode === 'create' && (
                <Box className="mb-4">
                    <Alert severity="info">
                        Un courriel sera automatiquement envoyé à l'utilisateur dès la création de son compte, l'invitant à finaliser le processus en fournissant son mot de passe.
                    </Alert>
                </Box>
            )}

            <Typography className={styles.SGStitle}>{t('utilisateur.intertitre.civilite')} :</Typography>
            <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
            <SGSForm.Row>
                <SGSForm.Col>
                    <SGSRadioCtr row options={titres} value={getValues('titre')} name="titre" className={'mb-n3'}></SGSRadioCtr>
                </SGSForm.Col>
            </SGSForm.Row>
            <SGSForm.Row>
                <SGSForm.Col md={6}>
                    <SGSFormInput variant="outlined" label="utilisateur.information.nom" name="nom" uppercase alphanumeric />
                </SGSForm.Col>
                <SGSForm.Col md={6}>
                    <SGSFormInput variant="outlined" label="utilisateur.information.prenom" name="prenom" uppercase alphanumeric />
                </SGSForm.Col>
            </SGSForm.Row>
            <SGSForm.Row>
                <SGSForm.Col>
                    <SGSFormInput variant="outlined" label="utilisateur.information.fonction" name="fonction" uppercase />
                </SGSForm.Col>
            </SGSForm.Row>
            <SGSForm.Row>
                <SGSForm.Col>
                    <SGSFormInput
                        variant="outlined"
                        label="utilisateur.information.email"
                        name="email"
                        autoComplete="no"
                        //disabled={selectedUser.id}
                        InputProps={{
                            endAdornment: isCheckingEmail && (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </SGSForm.Col>
            </SGSForm.Row>
            <SGSForm.Row>
                <SGSForm.Col md={6}>
                    <SGSFormInputMask variant="outlined" label="utilisateur.information.telephone" name="telephone" mask={'99.99.99.99.99'} />
                </SGSForm.Col>
            </SGSForm.Row>

            <Typography className={classNames(styles.SGStitle, 'mt-4')}>{t('utilisateur.profile')} :</Typography>
            <Divider className={classNames(styles.SGSDivider, 'mb-4')} />

            <SGSForm.Row>
                <SGSForm.Col>
                    <SGSRadioCtr
                        label="Profil de l'utilisateur"
                        row
                        options={rolesList}
                        value={role}
                        name="role"
                        handleChange={setRole}
                        disabled={selectedUser.role === TypeRole.ADMIN || isCurrentUser || mode === 'newUser'}
                    ></SGSRadioCtr>
                </SGSForm.Col>
            </SGSForm.Row>

            {/* {(selectedUser.role === 'UTILISATEUR' || role === 'UTILISATEUR') && !isCurrentUser && mode !== 'newUser' && ( */}
            {role === 'UTILISATEUR' && !isCurrentUser && mode !== 'newUser' && (
                <>
                    <Typography className={classNames(styles.SGStitle, 'mt-2')}>{t('utilisateur.intertitre.responsable')} :</Typography>
                    <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
                    <SGSForm.Row>
                        <SGSForm.Col>
                            <Controller
                                name={'responsable'}
                                control={control}
                                render={({ field: { ref }, fieldState: { error } }) => (
                                    <>
                                        <Autocomplete
                                            id={'responsable'}
                                            ref={ref}
                                            multiple={false}
                                            noOptionsText="Pas de résultat"
                                            disableListWrap
                                            fullWidth
                                            onChange={(_, data) => handleResponsableSelection(data)}
                                            options={responsables.filter((r) => r.id !== selectedUser.id)}
                                            value={watchResponsable}
                                            renderInput={(params) => (
                                                <TextField error={!!error} {...params} label={t('utilisateurs.list.responsable' as string)} helperText={error?.message} variant={'outlined'} />
                                            )}
                                            getOptionLabel={(option: any) => {
                                                if (option?.prenom) return option.prenom + ' ' + option.nom;
                                                return '';
                                            }}
                                        />
                                    </>
                                )}
                            />
                        </SGSForm.Col>
                    </SGSForm.Row>
                </>
            )}

            <Typography className={classNames(styles.SGStitle, 'mt-4')}>{t('utilisateur.intertitre.compte')}</Typography>
            <Divider className={classNames(styles.SGSDivider, 'mb-4')} />

            <SGSForm.Row>
                <SGSForm.Col>
                    <SGSFormInput
                        variant="outlined"
                        label="utilisateur.information.login"
                        name="login"
                        disabled
                        // onChange={handleLoginChange}
                        InputProps={{
                            endAdornment: isCheckingLogin && (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </SGSForm.Col>
            </SGSForm.Row>

            {!isCurrentUser && mode !== 'newUser' && (
                <SGSForm.Row>
                    <SGSForm.Col md={6}>
                        <SGSRadioCtr
                            label="Activer l'accès au site Internet"
                            row
                            options={accesWebOptions}
                            defaultValue={getValues('accesWeb')}
                            value={watch('accesWeb')}
                            name="accesWeb"
                            disabled={isCurrentUser}
                        ></SGSRadioCtr>
                    </SGSForm.Col>
                </SGSForm.Row>
            )}

            {mode === 'newUser' && (
                <SGSForm.Row>
                    <SGSForm.Col md={6}>
                        <SGSFormInput
                            type={'password'}
                            variant="outlined"
                            label="utilisateur.information.password"
                            name="password"
                            autoComplete="new-password"
                            inputProps={{
                                maxLength: 15,
                            }}
                        />
                        <Box className="mt-n3 small">
                            <small className="text-muted">*15 caractères maximum.</small>
                        </Box>
                    </SGSForm.Col>
                    <SGSForm.Col md={6}>
                        <SGSFormInput
                            type={'password'}
                            variant="outlined"
                            label="utilisateur.information.confirmation"
                            name="confirmation"
                            inputProps={{
                                maxLength: 15,
                            }}
                        />
                    </SGSForm.Col>
                </SGSForm.Row>
            )}
        </>
    );
};
